import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import * as Sentry from '@sentry/angular';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService, ToastData } from './notification.service';

export type MessageCode = string;
type KnownMessageCode = 'UNAUTHORIZED' | 'INVALID_TOKEN' | 'FORBIDDEN' | 'USER_NOT_FOUND' | 'INTERNAL_SERVER_ERROR';

export interface ApiError {
    message: string;
    code?: number;
    messageCode?: MessageCode;
    details?: any;
    timestamp: Date;
    path?: string;
    shouldLogout?: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class ErrorHandlerService {
    private notificationService = inject(NotificationService);
    private translate = inject(TranslateService);

    private readonly LEGACY_MESSAGE_CODE_KEYS: Record<KnownMessageCode, string> = {
        UNAUTHORIZED: 'httpErrors.unauthorized',
        INVALID_TOKEN: 'httpErrors.invalidToken',
        FORBIDDEN: 'httpErrors.forbidden',
        USER_NOT_FOUND: 'httpErrors.userNotFound',
        INTERNAL_SERVER_ERROR: 'httpErrors.serverError',
    };

    private translateMessageCode(code: string, params?: Record<string, unknown>): string | null {
        const key = `messageCodes.${code}`;
        const translated = this.translate.instant(key, params);
        return translated === key ? null : translated;
    }

    /** `retryAfterSeconds` sits at the root of the body (e.g. RELOAD_COOLDOWN); rounded up, never below 1 minute. */
    private retryAfterMinutes(error: HttpErrorResponse): number {
        const seconds = Number(error.error?.retryAfterSeconds);
        return Number.isFinite(seconds) && seconds > 0 ? Math.max(1, Math.ceil(seconds / 60)) : 1;
    }

    handleError(error: HttpErrorResponse): ApiError {
        const messageCode = error.error?.message_code as MessageCode | undefined;

        const apiError: ApiError = {
            message: this.translate.instant('httpErrors.default'),
            code: error.status,
            messageCode,
            timestamp: new Date(),
            path: error.url || undefined,
            shouldLogout: messageCode === 'INVALID_TOKEN' || error.error?.shouldLogout === true,
        };

        let toastData: ToastData = {
            duration: 5000,
            type: 'warning',
            isExpandable: false,
            subject: this.translate.instant('httpErrors.subject'),
            message: apiError.message,
        };

        // Gestion prioritaire selon le message_code de l'API.
        // RATE_LIMITED est exclu : son message dépend de retryAfterSeconds, géré par le switch ci-dessous.
        // Les 422 sont exclus : le message_code racine ne reflète que le premier champ en erreur,
        // extractValidationErrors doit lire errors_details pour couvrir tous les champs fautifs.
        if (messageCode && messageCode !== 'RATE_LIMITED' && error.status !== 422) {
            const legacyKey = this.LEGACY_MESSAGE_CODE_KEYS[messageCode as KnownMessageCode];
            const resolvedMessage = legacyKey
                ? this.translate.instant(legacyKey)
                : this.translateMessageCode(messageCode, { minutes: this.retryAfterMinutes(error) });

            if (resolvedMessage) {
                apiError.message = resolvedMessage;
                this.notificationService.showToast(
                    {
                        ...toastData,
                        message: apiError.message,
                    },
                    'warning',
                );
                apiError.details = error.error;
                return apiError;
            }
        }

        // Gestion selon le code d'erreur HTTP
        switch (error.status) {
            case 0:
                apiError.message = this.translate.instant('httpErrors.networkError');
                this.notificationService.showToast(
                    {
                        ...toastData,
                        message: apiError.message,
                    },
                    'warning',
                );
                break;

            case 400:
                apiError.message = this.translate.instant('httpErrors.badRequest');
                this.notificationService.showToast(
                    {
                        ...toastData,
                        message: apiError.message,
                    },
                    'warning',
                );
                break;

            case 401:
                apiError.message = this.translate.instant('httpErrors.unauthorized');
                this.notificationService.showToast(
                    {
                        ...toastData,
                        message: apiError.message,
                    },
                    'warning',
                );
                break;

            case 403:
                apiError.message = this.translate.instant('httpErrors.forbidden');
                this.notificationService.showToast(
                    {
                        ...toastData,
                        message: apiError.message,
                    },
                    'warning',
                );
                break;

            case 404:
                apiError.message = this.translate.instant('httpErrors.notFound');
                this.notificationService.showToast(
                    {
                        ...toastData,
                        message: apiError.message,
                    },
                    'warning',
                );
                break;

            case 409:
                apiError.message = this.translate.instant('httpErrors.conflict');
                this.notificationService.showToast(
                    {
                        ...toastData,
                        message: apiError.message,
                    },
                    'warning',
                );
                break;

            case 422:
                apiError.message = this.extractValidationErrors(error);
                toastData = {
                    ...toastData,
                    message: apiError.message,
                    isExpandable: true,
                    subject: this.translate.instant('httpErrors.subjectValidation'),
                    error_value: error.error,
                };
                this.notificationService.showToast(toastData, 'warning');
                break;

            case 429:
                apiError.message = this.translate.instant('httpErrors.tooManyRequests');
                this.notificationService.showToast(
                    {
                        ...toastData,
                        message: apiError.message,
                    },
                    'warning',
                );
                break;

            case 500:
            case 502:
            case 503:
                apiError.message = this.translate.instant('httpErrors.serverError');
                this.notificationService.showToast(
                    {
                        ...toastData,
                        message: apiError.message,
                    },
                    'warning',
                );
                {
                    // Stable message + fingerprint (status and route, ids stripped): the HTML body and the full
                    // URL used to split or merge unrelated outages into the same issue.
                    const route = this.routeOf(error.url);
                    Sentry.captureException(new Error(`Server error ${error.status} on ${route}`), {
                        fingerprint: ['http-server-error', String(error.status), route],
                        extra: { url: error.url, status: error.status, body: error.error, message: this.extractErrorMessage(error) || error.message },
                    });
                }
                break;

            case 504:
                apiError.message = this.translate.instant('httpErrors.timeout');
                this.notificationService.showToast(
                    {
                        ...toastData,
                        message: apiError.message,
                    },
                    'warning',
                );
                break;

            default:
                apiError.message = this.translate.instant('httpErrors.default');
                this.notificationService.showToast(
                    {
                        ...toastData,
                        message: apiError.message,
                    },
                    'warning',
                );
        }

        apiError.details = error.error;
        return apiError;
    }

    /** Pathname of the failing URL with UUIDs and numeric segments replaced by `:id`. */
    private routeOf(url: string | null): string {
        if (!url) return 'unknown url';
        let pathname = url;
        try {
            pathname = new URL(url, window.location.origin).pathname;
        } catch {
            // keep the raw value
        }
        return pathname.replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ':id').replace(/\/\d+(?=\/|$)/g, '/:id');
    }

    private extractErrorMessage(error: HttpErrorResponse): string {
        if (error.error?.message) {
            return error.error.message;
        }
        if (error.error?.error) {
            return error.error.error;
        }
        if (typeof error.error === 'string') {
            return error.error;
        }
        return '';
    }

    private extractValidationErrors(error: HttpErrorResponse): string {
        const details = error.error?.errors_details;
        if (Array.isArray(details) && details.length > 0) {
            const messages = details
                .map((d: { field?: string; error_code?: string; error_message?: string }) => {
                    const text = (d.error_code && this.translateMessageCode(d.error_code)) ?? d.error_message ?? '';
                    return d.field && text ? `${d.field}: ${text}` : text;
                })
                .filter((msg) => msg !== '');

            if (messages.length > 0) {
                return messages.join(' | ');
            }
        }

        if (error.error?.errors) {
            const errors = error.error.errors;
            if (Array.isArray(errors)) {
                const messages = errors
                    .map((errorObj) => {
                        if (typeof errorObj === 'string') {
                            return errorObj;
                        }
                        if (typeof errorObj === 'object') {
                            return Object.entries(errorObj)
                                .map(([field, message]) => `${field}: ${message}`)
                                .join(', ');
                        }
                        return '';
                    })
                    .filter((msg) => msg !== '');

                return messages.join(' | ');
            }
            if (typeof errors === 'object') {
                return Object.values(errors).flat().join(', ');
            }
        }
        return this.extractErrorMessage(error) || this.translate.instant('httpErrors.validationFallback');
    }
}
